import { useEffect, useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import { useAtomValue, useSetAtom } from 'jotai';

import type { LatestImage } from '@/@types/latestImages';
import type { ImageAnalyses } from '@/@types/latestImagesV2';
import { generateFmxGrid } from '@/modules/claims/cleanClaims/helpers';
import { claimAttachmentIdAtom } from '@/modules/claims/freeClaims/atoms';
import type { GetClaimSummaryResponse } from '@/modules/claims/freeClaims/insuranceExport/types';
import {
  claimAttachmentToothLevelImagesAtom,
  cleanClaimsAttachmentValuesAtom,
  narrativeAtom,
  selectedTreatmentsAtom,
} from '@/modules/claims/freeClaims/state';
import { useSaveAttachmentAudit } from '@/react-query/rcm/useSaveAttachmentAudit';
import { usePreferences } from '@/react-query/usePreferences';

import { ClaimFooter } from './ClaimFooter';
import { ClaimFooterPrintVersion } from './ClaimFooterPrintVersion';
import { ClaimHeader } from './ClaimHeader';
import { ClaimNarrative } from './ClaimNarrative';
import { PerioChart } from './insuranceImagesSection/PerioChart/PerioChart';
import { QuadLevelImagesGroup } from './insuranceImagesSection/QuadLevelImagesGroup';
import { ToothLevelImagesGroup } from './insuranceImagesSection/ToothLevelImagesGroup';

export type TTreatmentDisplay = { cdtCode?: string; quadrant?: string };
export type GroupedTeethImages = LatestImage[];

type ClaimAttachmentContentProps = {
  claimSummary?: GetClaimSummaryResponse;
  filteredColorImages?: ImageAnalyses[];
  filteredRadiographs?: ImageAnalyses[];
  hasToothLevelSummary: boolean;
  hasQuadLevelSummary: boolean;
  isLoadingClaimSummary: boolean;
  isPrintMode: boolean;
};

export const ClaimAttachmentContent = ({
  claimSummary,
  filteredColorImages,
  filteredRadiographs,
  hasToothLevelSummary,
  hasQuadLevelSummary,
  isLoadingClaimSummary,
  isPrintMode,
}: ClaimAttachmentContentProps) => {
  // atoms
  const selectedTreatmentIds = useAtomValue(selectedTreatmentsAtom);
  const narrativeText = useAtomValue(narrativeAtom);
  const cleanClaimsAttachmentValues = useAtomValue(
    cleanClaimsAttachmentValuesAtom,
  );
  const toothLevelImages = useAtomValue(claimAttachmentToothLevelImagesAtom);

  const setClaimAttachmentId = useSetAtom(claimAttachmentIdAtom);

  // hooks
  const { data: prefs } = usePreferences();
  const { mutate: saveAuditEntry } = useSaveAttachmentAudit();

  // Check if tooth level images have annotations/table data
  const hasToothLevelAnnotations = useMemo(() => {
    const hasAnnotations = (annotations: any) => {
      if (!annotations) return false;
      return Object.values(annotations).some((arr: any) => arr?.length > 0);
    };

    // Check if any tooth level image has annotations or table data (which indicates actual findings)
    return toothLevelImages.some(
      (image) =>
        hasAnnotations(image.annotations) || image.tableData?.length > 0,
    );
  }, [toothLevelImages]);

  // variables
  const fmxGrid = useMemo(
    () => generateFmxGrid(claimSummary, selectedTreatmentIds ?? []),
    [claimSummary, selectedTreatmentIds],
  );

  const filteredQuadLevelImages = useMemo(
    () => fmxGrid.filter((img) => img !== null) as ImageAnalyses[],
    [fmxGrid],
  );

  const { estimatedVisitId, pmsAppointmentId } = cleanClaimsAttachmentValues;

  const showNarrative = prefs?.cleanClaims;

  // useEffects
  // save audit entry and generate claim attachment id
  useEffect(() => {
    if (
      !isLoadingClaimSummary &&
      (hasToothLevelSummary || hasQuadLevelSummary)
    ) {
      const imageIds = filteredQuadLevelImages
        ?.concat(filteredRadiographs ?? [])
        .concat(filteredColorImages ?? [])
        .map((i) => i.imageId);
      saveAuditEntry(
        {
          estimatedVisitId: estimatedVisitId
            ? parseInt(estimatedVisitId)
            : undefined,
          pmsAppointmentId: pmsAppointmentId,
          imageIds: imageIds,
          narrative: narrativeText,
          view: 'preview-single',
          options: {
            includeAllImages: false,
            bitewingsOnly: false,
            includePrlFindings: false,
            includeCariesFindings: hasToothLevelSummary,
            includeDmfFindings: hasToothLevelSummary,
            includeRblAndCalculusFindings: hasQuadLevelSummary,
          },
        },
        {
          onSuccess: (data: { data: { attachmentId: string } }) => {
            if (data?.data?.attachmentId) {
              setClaimAttachmentId(data.data.attachmentId);
            }
          },
        },
      );
    }
  }, [
    narrativeText,
    estimatedVisitId,
    pmsAppointmentId,
    filteredColorImages,
    filteredRadiographs,
    filteredQuadLevelImages,
    isLoadingClaimSummary,
    hasToothLevelSummary,
    hasQuadLevelSummary,
    saveAuditEntry,
    setClaimAttachmentId,
  ]);

  return (
    <div
      data-testid="claim-attachment-content"
      className={`vai-max-w-[1594px] vai-h-fit ${
        !isPrintMode ? 'vai-p-8 vai-bg-white vai-border' : ''
      }`}
    >
      {isPrintMode ? (
        <div
          data-testid="print-mode-container"
          className="vai-flex vai-flex-col vai-gap-8"
        >
          {/* First page */}
          <div
            data-testid="print-first-page"
            className="vai-bg-white vai-border vai-p-8 vai-min-h-[1056px] vai-flex vai-flex-col vai-gap-6"
          >
            <div className="vai-flex vai-flex-col vai-gap-6">
              <h2
                data-testid="print-title"
                className="vai-text-xl vai-font-bold"
              >
                <Trans>Claim Attachment</Trans>
              </h2>

              <ClaimHeader />
              {showNarrative && <ClaimNarrative />}
            </div>
            {hasToothLevelSummary && filteredRadiographs?.[0] ? (
              <div className="vai-mt-2">
                <ToothLevelImagesGroup
                  filteredRadiographs={[filteredRadiographs[0]]}
                  filteredColorImages={[]}
                  claimSummary={claimSummary}
                />
              </div>
            ) : hasQuadLevelSummary ? (
              <div className="vai-mt-2">
                <QuadLevelImagesGroup
                  fmxGrid={fmxGrid}
                  filteredImages={filteredQuadLevelImages}
                  claimSummary={claimSummary}
                  showOriginal
                />
              </div>
            ) : null}
          </div>

          {/* Additional pages for remaining tooth level images */}
          {hasToothLevelSummary &&
            filteredRadiographs &&
            filteredRadiographs.slice(1).map((radiograph, index, array) => (
              <div
                key={index}
                className="vai-bg-white vai-border vai-p-8 vai-min-h-[1056px] vai-flex vai-flex-col vai-gap-6"
              >
                <ToothLevelImagesGroup
                  filteredRadiographs={[radiograph]}
                  filteredColorImages={[]}
                  claimSummary={claimSummary}
                />
                {index === array.length - 1 && hasToothLevelAnnotations && (
                  <div className="vai-mt-auto">
                    <ClaimFooterPrintVersion type="dmf" />
                    <ClaimFooterPrintVersion type="resto" />
                  </div>
                )}
              </div>
            ))}

          {/* Quad Level Images Pages */}
          {hasQuadLevelSummary && (
            <>
              {/* Non-annotated quad level images page (should be right before annotated/table page) */}
              <div className="vai-bg-white vai-border vai-p-8 vai-min-h-[1056px] vai-flex vai-flex-col vai-gap-6">
                <QuadLevelImagesGroup
                  fmxGrid={fmxGrid}
                  filteredImages={filteredQuadLevelImages}
                  claimSummary={claimSummary}
                  showOriginal
                />
              </div>

              {/* Annotated quad level images page with table */}
              <div className="vai-bg-white vai-border vai-p-8 vai-min-h-[1056px] vai-flex vai-flex-col vai-gap-6">
                <QuadLevelImagesGroup
                  fmxGrid={fmxGrid}
                  filteredImages={filteredQuadLevelImages}
                  claimSummary={claimSummary}
                  showAnnotations
                  showTable
                />
              </div>

              {/* Perio Chart */}
              {!!claimSummary?.perioChart?.teeth?.length && (
                <div className="vai-bg-white vai-border vai-p-8 vai-min-h-[1056px] vai-flex vai-flex-col vai-gap-6">
                  <PerioChart
                    perioData={claimSummary.perioChart.teeth}
                    examDate={claimSummary.perioChart.examDate}
                  />
                </div>
              )}

              {/* Footer page */}
              <div className="vai-bg-white vai-border vai-p-8 vai-min-h-[1056px] vai-flex vai-flex-col vai-gap-6">
                <div className="vai-flex vai-flex-col vai-gap-8">
                  <ClaimFooterPrintVersion type="rbl" />
                  <ClaimFooterPrintVersion type="display" />
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <h2 data-testid="preview-title" className="vai-text-xl vai-font-bold">
            <Trans>Claim Attachment</Trans>
          </h2>
          <div
            data-testid="preview-content"
            className="vai-flex vai-flex-col vai-gap-6 vai-mt-6"
          >
            <ClaimHeader />
            <div
              data-testid="preview-sections"
              className="vai-flex vai-flex-col vai-gap-8"
            >
              {showNarrative && <ClaimNarrative />}
              {hasToothLevelSummary && (
                <ToothLevelImagesGroup
                  filteredRadiographs={filteredRadiographs ?? []}
                  filteredColorImages={filteredColorImages ?? []}
                  claimSummary={claimSummary}
                />
              )}
              {hasQuadLevelSummary && (
                <QuadLevelImagesGroup
                  fmxGrid={fmxGrid}
                  filteredImages={filteredQuadLevelImages}
                  claimSummary={claimSummary}
                  showAnnotations
                  showTable
                  showOriginal
                />
              )}
              {!!claimSummary?.perioChart?.teeth?.length && (
                <PerioChart
                  perioData={claimSummary.perioChart.teeth}
                  examDate={claimSummary.perioChart.examDate}
                />
              )}
              <ClaimFooter
                showClinicalValuesExplanation={hasQuadLevelSummary}
                showDmfExplanation={hasToothLevelAnnotations}
                showRestoExplanation={hasToothLevelAnnotations}
                showRblExplanation={hasQuadLevelSummary}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
